import { useState } from 'react';

import { Button } from '@components/button';
import { IconButton } from '@components/icon-button';
import { IconApplicants } from '@components/icons/applicants';
import { IconDirectionDown } from '@components/icons/direction-down';
import { IconDirectionRight } from '@components/icons/direction-right';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { IApplication } from 'models/types';

import { useH3BulkCreate } from './hooks';

export const useBulkCreateH3CoreSidebar = () => useSidebar(BulkCreateH3Core);

type BulkCreateCoreProps = {
  applicants: IApplication[];
};

export const BulkCreateH3Core = (props: BulkCreateCoreProps) => {
  const { t } = useScopedTranslate(locale);
  const [isOpen, setIsOpen] = useState(false);
  const createMutation = useH3BulkCreate();

  const onSuccess = async () => {
    hideSidebar();
  };

  const handleSubmit = () => {
    const data = {
      applicationIds: props.applicants.map((applicant) => applicant.id),
      onSuccess,
    };
    createMutation.mutate(data);
  };

  if (props.applicants.length === 0) {
    return (
      <Sidebar>
        <Sidebar.SimpleHeader title={t('bulkCreateH3')} />
        <Sidebar.Content>
          <Text text={t('noValidApplicants')} />
        </Sidebar.Content>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('bulkCreateH3')} />
      <Sidebar.Content>
        <Stack space={6}>
          <Stack space={4}>
            <Stack orientation="horizontal" justify="space-between">
              <Stack space={2} orientation="horizontal" align="center">
                <IconApplicants color="purple50" />
                <Text
                  text={t('selectedApplicants', props.applicants.length.toString())}
                  font="bold14"
                />
              </Stack>
              {isOpen ? (
                <IconButton
                  onClick={() => setIsOpen(false)}
                  icon={<IconDirectionDown />}
                  variant="ghost-purple"
                />
              ) : (
                <IconButton
                  onClick={() => setIsOpen(true)}
                  icon={<IconDirectionRight />}
                  variant="ghost-purple"
                />
              )}
            </Stack>
            {isOpen ? (
              <Stack>
                {props.applicants.map((applicant) => (
                  <Text font="bold14" text={applicant.name} key={applicant.id} />
                ))}
              </Stack>
            ) : null}
          </Stack>
          <Text text={t('description')} />

          <Stack orientation="horizontal" space={4} justify="flex-end">
            <Button variant="secondary" onClick={hideSidebar} text={t('cancel')} />
            <Button
              variant="primary"
              onClick={handleSubmit}
              text={t('create')}
              disabled={createMutation.isLoading}
            />
          </Stack>
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const locale = {
  en: {
    bulkCreateH3: 'Bulk create H3',
    selectedApplicants: 'Selected applicant(s) • {0}',
    create: 'Create',
    cancel: 'Cancel',
    noValidApplicants:
      'There are no valid applicants in the selection you have made. Please make sure the applications are marked as hired and have not already been created in H3',
    description:
      'Here above is a list of applicants that will be created or updated (if they already exist) in H3.\nPlease review all applicants beforehand and make sure all relevant H3 fields have been filled out correctly as the integration will not create these individuals if they are missing required fields.\nThis might take a few minutes.',
  },
  is: {
    bulkCreateH3: 'Stofna í H3',
    selectedApplicants: 'Valdnar umsóknir • {0}',
    create: 'Stofna',
    cancel: 'Hætta við',
    noValidApplicants:
      'Engar gildar umsóknir hafa verið valdnar. Vinsamlegast athugið að einungis umsóknir sem hafa verið merktar ráðnar og hafa ekki nú þegar verið stofnaðir í H3 eru gildar í þessa aðgerð',
    description:
      'Hér fyrir ofan er listi af umsækjendum sem verða stofnaðir eða uppfærðir (ef þeir eru nú þegar til) í H3.\nVinsamlegast farið yfir alla umsækjendur áður til að ganga úr skugga um að allir viðeigandi H3 reitir séu rétt fylltir út þar sem umsækjendur verða ekki stofnaðir ef það vantar skilyrtar upplýsingar.\nÞetta gæti tekið nokkrar mínútur.',
  },
};
